import { Link } from 'react-router-dom';
import { Heart, Users, Shield, ChevronRight, AlertCircle } from 'lucide-react';

const DONATION_URL = "https://fec1af13-e154-40f6-9732-ef75aabe7b90.paylinks.godaddy.com/d80cd3ea-dba0-4acc-9995-dc0";

const IMAGES = {
  veteranSupport: "https://images.pexels.com/photos/7467907/pexels-photo-7467907.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  flagBackground: "https://images.pexels.com/photos/1340504/pexels-photo-1340504.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
};

export const DonationPage = () => {
  const impactAreas = [
    {
      icon: Users,
      title: 'Direct Veteran Support',
      description: 'Help us provide personalized guidance and support to veterans navigating the complex VA claims process.'
    },
    {
      icon: Shield,
      title: 'Educational Resources',
      description: 'Fund the development of guides, workshops, and materials that empower veterans with knowledge.'
    },
    {
      icon: Heart,
      title: 'Community Outreach',
      description: 'Support our efforts to reach more veterans who need assistance but may not know where to turn.'
    }
  ];

  return (
    <article className="page-transition" data-testid="donation-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-wv-blue" data-testid="donation-hero">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={IMAGES.flagBackground}
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-wv-red rounded-full flex items-center justify-center">
              <Heart className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Support a Veteran
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
            Your generosity helps us continue our mission to ensure every veteran receives 
            the support and benefits they rightfully earned.
          </p>
          <a
            href={DONATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            data-testid="hero-donate-button"
          >
            <Heart className="w-5 h-5" />
            Make a Donation
            <ChevronRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Disclaimer Banner */}
      <section className="bg-amber-50 border-y border-amber-200 py-4" data-testid="disclaimer-banner">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 text-base">
              <strong>Important Notice:</strong> Worthy Vets Service Group is not a 501(c)(3) nonprofit. 
              Contributions are voluntary and not tax-deductible.
            </p>
          </div>
        </div>
      </section>

      {/* Why Donate Section */}
      <section className="py-20 bg-white" data-testid="why-donate-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-wv-blue mb-4">
              Why Your Support Matters
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Every contribution helps us serve more veterans and their families 
              as they navigate the VA benefits process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {impactAreas.map((area, index) => (
              <div
                key={index}
                className="card-tactile rounded-lg p-8 text-center hover:shadow-lg transition-all"
                data-testid={`impact-card-${index}`}
              >
                <div className="w-16 h-16 bg-wv-blue/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <area.icon className="w-8 h-8 text-wv-red" />
                </div>
                <h3 className="font-heading text-xl font-bold text-wv-blue mb-4">
                  {area.title}
                </h3>
                <p className="text-gray-600">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-paper-white" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={IMAGES.veteranSupport}
                alt="Veterans supporting each other"
                className="rounded-lg shadow-xl w-full"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-wv-blue mb-6">
                Together, We Make a Difference
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Many veterans struggle to navigate the complex VA claims process alone. 
                They may not understand what benefits they're entitled to, or they may 
                feel overwhelmed by the paperwork and procedures.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Your support allows us to provide the guidance, resources, and encouragement 
                that can make all the difference in a veteran's journey to receiving the 
                benefits they earned through their service.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Whether it's helping a veteran file their first claim, assisting with an 
                appeal, or simply providing a listening ear, your contribution helps us 
                be there for those who served our country.
              </p>
              <a
                href={DONATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="story-donate-button"
              >
                <Heart className="w-5 h-5" />
                Support Our Mission
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-wv-blue" data-testid="donation-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-16 h-16 text-wv-red mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Every Contribution Helps
          </h2>
          <p className="text-xl text-white/80 mb-4 max-w-2xl mx-auto">
            No amount is too small. Your support shows veterans that their community 
            stands behind them.
          </p>
          <p className="text-sm text-white/60 mb-8 max-w-xl mx-auto">
            Worthy Vets Service Group is not a 501(c)(3) nonprofit. 
            Contributions are voluntary and not tax-deductible.
          </p>
          <a
            href={DONATION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            data-testid="cta-donate-button"
          >
            <Heart className="w-5 h-5" />
            Donate Now
            <ChevronRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Other Ways to Help */}
      <section className="py-16 bg-white" data-testid="other-ways-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-heading text-2xl font-bold text-wv-blue mb-4">
            Other Ways to Support Veterans
          </h3>
          <p className="text-lg text-gray-600 mb-8">
            Can't donate right now? You can still make a difference by spreading the word 
            about our services to veterans who may need assistance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/services"
              className="btn-secondary inline-flex items-center justify-center"
            >
              Learn About Our Services
            </Link>
            <Link
              to="/contact"
              className="btn-secondary inline-flex items-center justify-center"
            >
              Get In Touch
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
};
